import { useCallback, useEffect, useRef, useState } from 'react';

// Higher-order component to wrap scrollable content with carousel UI and behavior.
// Props allow overriding class names and aria labels for reuse across views.

const calculateScrollStops = (pageWidth, maxLeft, isOverflowing) => {
  const stops = [0];
  if (isOverflowing && pageWidth > 0 && maxLeft > 0) {
    for (let pos = pageWidth; pos < maxLeft; pos += pageWidth) {
      stops.push(pos);
    }
    stops.push(maxLeft);
  }

  return stops;
};

const getCarouselStops = ({ scrollWidth, clientWidth }) => {
  const pageWidth = Math.max(1, clientWidth || 0);
  const maxLeft = Math.max(0, (scrollWidth || 0) - pageWidth);
  const isOverflowing = maxLeft > 0;

  const stops = calculateScrollStops(pageWidth, maxLeft, isOverflowing);

  return { pageWidth, maxLeft, isOverflowing, stops };
};

export const withCarousel = (Component) => {
  // eslint-disable-next-line react/display-name
  return ({
    enabled = true,
    sectionClass = 'carousel-section',
    wrapperClass = 'carousel-wrapper',
    scrollerClass = 'scan-cards-carousel',
    dotsClass = 'carousel-dots',
    dotClass = 'carousel-dot',
    activeDotClass = 'active',
    dotsAriaLabel = 'Carousel pages',
    deps = [],
    ...props
  }) => {
    const carouselRef = useRef(null);
    const [carouselUi, setCarouselUi] = useState({
      isOverflowing: false,
      canScrollLeft: false,
      canScrollRight: false,
      pageCount: 1,
      pageIndex: 0
    });

    const updateCarouselUi = useCallback(() => {
      const el = carouselRef.current;
      if (!el) {
        return;
      }

      const { scrollLeft } = el;
      const { maxLeft, isOverflowing, stops } = getCarouselStops(el);

      const pageCount = stops.length;
      let nearestIndex = 0;
      let nearestDist = Infinity;
      for (let i = 0; i < stops.length; i++) {
        const d = Math.abs(scrollLeft - stops[i]);
        if (d < nearestDist) {
          nearestDist = d;
          nearestIndex = i;
        }
      }

      const pageIndex = nearestIndex;
      const canScrollLeft = isOverflowing && scrollLeft > 0;
      const canScrollRight = isOverflowing && scrollLeft < maxLeft;

      setCarouselUi((prev) => {
        if (
          prev.isOverflowing === isOverflowing &&
          prev.canScrollLeft === canScrollLeft &&
          prev.canScrollRight === canScrollRight &&
          prev.pageCount === pageCount &&
          prev.pageIndex === pageIndex
        ) {
          return prev;
        }
        return {
          isOverflowing,
          canScrollLeft,
          canScrollRight,
          pageCount,
          pageIndex
        };
      });
    }, []);

    useEffect(() => {
      if (!enabled) {
        return;
      }

      const el = carouselRef.current;
      if (!el) {
        return;
      }

      // We measure `scrollWidth/clientWidth/scrollLeft` to derive page stops.
      // We schedule a single `requestAnimationFrame` so the first measurement happens after
      // the initial paint. If layout changes later (fonts/images/children), the existing
      // `ResizeObserver` (when available) and scroll listener will re-run the measurement.
      const raf1 = requestAnimationFrame(() => updateCarouselUi());

      el.addEventListener('scroll', updateCarouselUi, { passive: true });

      let resizeObserver;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver((entries, observer) => {
          void entries;
          void observer;
          updateCarouselUi();
        });
        resizeObserver.observe(el);
      }

      return () => {
        el.removeEventListener('scroll', updateCarouselUi);
        cancelAnimationFrame(raf1);
        if (resizeObserver) resizeObserver.disconnect();
      };
      // `deps` is an escape hatch for callers (a manual dependency list passed in by
      // the parent component): include values that change the
      // carousel's scroll geometry (e.g. item count / async content) so we
      // re-measure even if the scroller element itself didn't resize.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, updateCarouselUi, ...deps]);

    const handleDotClick = (pageIndex) => {
      const el = carouselRef.current;
      if (!el) {
        return;
      }

      const { stops } = getCarouselStops(el);

      const idx = Math.min(stops.length - 1, Math.max(0, pageIndex));
      el.scrollLeft = stops[idx];
    };

    return (
      <div className={sectionClass}>
        <div className={wrapperClass}>
          <div className={scrollerClass} ref={carouselRef}>
            <Component {...props} />
          </div>
        </div>

        {carouselUi.isOverflowing && carouselUi.pageCount > 1 && (
          <div className={dotsClass} role="tablist" aria-label={dotsAriaLabel}>
            {Array.from({ length: carouselUi.pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={`${dotClass} ${index === carouselUi.pageIndex ? activeDotClass : ''}`}
                onClick={() => handleDotClick(index)}
                aria-current={index === carouselUi.pageIndex ? 'true' : undefined}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
};

export default withCarousel;
